import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import {
  distanceKmToAccuracyPct,
  EARTH_MODES,
  EARTH_MODE_IMPOSSIBLE,
  EARTH_MODE_IMPOSSIBLE_ISH,
  EARTH_RIDDLE_MACHINE_COST,
  getEarthZoomLevels,
  isEarthGuessCorrect,
  ZOOMED_IN_EARTH_COST,
} from '../../../constants/games/zoomedInEarthGame';
import {
  GUEST_DEMO_BUTTON_LABEL,
  EARTH_DEMO_NOTE,
} from '../../../constants/games/demoModeCopy';
import { pickRandomEarthDemoRound } from '../../../constants/games/zoomedInEarthDemo';
import { haversineDistanceKm } from '../../../utils/games/geoDistance';
import {
  requestEarthRiddle,
  revealEarthRound,
  resumeEarthRound,
  startEarthRound,
  submitEarthGuess,
} from '../../../utils/earthApi';
import { finishRound, startRound } from '../../../utils/gameRoundApi';
import { useActiveTimer } from '../../../utils/hooks/useActiveTimer';
import RewardSummary from '../shared/RewardSummary';
import EarthAverageGuessSummary from './EarthAverageGuessSummary';
import EarthGuessMap from './EarthGuessMap';
import EarthModeSelector from './EarthModeSelector';
import EarthResultsPanel from './EarthResultsPanel';
import './ZoomedInEarthGame.css';

const INITIAL_ZOOM_REDUCTION_RATIO = 0.5;

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const getDisplayZoom = (zoomLevel, revealStep) => {
  const zoom = Number(zoomLevel);
  if (!Number.isFinite(zoom)) return 100;
  if (revealStep !== 0) return zoom;
  return Math.max(100, zoom * INITIAL_ZOOM_REDUCTION_RATIO);
};

const createInitialRound = (mode = EARTH_MODE_IMPOSSIBLE_ISH) => ({
  roundId: '',
  mode,
  status: 'idle',
  maxAttempts: 1,
  attemptsUsed: 0,
  revealStep: 0,
  zoomLevels: getEarthZoomLevels(mode),
  imageDataUrl: '',
});

const ZoomedInEarthGame = () => {
  const { user, coins, refreshCoins, setSeasonPoints, setLifetimePoints } = useProfile();

  const [selectedMode, setSelectedMode] = useState(EARTH_MODE_IMPOSSIBLE_ISH);
  const [phase, setPhase] = useState('idle'); // idle | loading | playing | finished
  const [round, setRound] = useState(createInitialRound(EARTH_MODE_IMPOSSIBLE_ISH));
  const [guesses, setGuesses] = useState([]);
  const [selectedGuess, setSelectedGuess] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState('');
  const [isSubmittingGuess, setIsSubmittingGuess] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const [firstGameBonus, setFirstGameBonus] = useState(false);
  const [earthGuessAverages, setEarthGuessAverages] = useState(null);
  const [earthRiddle, setEarthRiddle] = useState('');
  const [earthRiddleError, setEarthRiddleError] = useState('');
  const [isLoadingEarthRiddle, setIsLoadingEarthRiddle] = useState(false);
  const [isDemoRound, setIsDemoRound] = useState(false);
  const [demoAnswer, setDemoAnswer] = useState(null);

  const roundTrackingIdRef = useRef(null);
  const lastDemoRoundIdRef = useRef('');
  const {
    start: startActiveTimer,
    stop: stopActiveTimer,
    getElapsed: getActiveElapsed,
  } = useActiveTimer();

  const coinsSafe = typeof coins === 'number' ? coins : 0;
  const canAfford = !user || coinsSafe >= ZOOMED_IN_EARTH_COST;
  const canAffordRiddleMachine = Boolean(user) && coinsSafe >= EARTH_RIDDLE_MACHINE_COST;
  const selectedModeLabel = useMemo(() => {
    const matchingMode = EARTH_MODES.find((modeOption) => modeOption.key === selectedMode);
    return matchingMode?.label || 'Impossible-ish';
  }, [selectedMode]);

  const currentZoom = useMemo(() => {
    const fallback = getEarthZoomLevels(round.mode);
    const levels = Array.isArray(round.zoomLevels) && round.zoomLevels.length > 0
      ? round.zoomLevels
      : fallback;
    const index = Math.max(0, Math.min(round.revealStep || 0, levels.length - 1));
    return levels[index] ?? 100;
  }, [round.mode, round.revealStep, round.zoomLevels]);
  const displayZoom = useMemo(
    () => getDisplayZoom(currentZoom, round.revealStep),
    [currentZoom, round.revealStep],
  );

  const latestGuess = guesses.length > 0 ? guesses[guesses.length - 1] : null;

  useEffect(() => {
    if (phase !== 'playing') return undefined;

    const id = window.setInterval(() => {
      setElapsedMs(getActiveElapsed());
    }, 250);
    return () => window.clearInterval(id);
  }, [getActiveElapsed, phase]);

  const beginLeaderboardTracking = useCallback((gameType) => {
    startActiveTimer();
    setElapsedMs(0);
    startRound({ gameType }).then((id) => {
      roundTrackingIdRef.current = id;
    });
  }, [startActiveTimer]);

  const finalizeLeaderboardTracking = useCallback(({
    won,
    guessesUsed,
    finalDistanceKm,
    finalAccuracyPct,
    bestDistanceKm,
    bestAccuracyPct,
    mode,
  }) => {
    const activeMs = stopActiveTimer();
    setElapsedMs(activeMs);

    const trackingRoundId = roundTrackingIdRef.current;
    if (!trackingRoundId) return;

    finishRound({
      roundId: trackingRoundId,
      won,
      guessesUsed,
      activeTimeMs: activeMs,
      metadata: {
        mode,
        final_distance_km: finalDistanceKm,
        final_accuracy_pct: finalAccuracyPct,
        final_guess_rate: finalAccuracyPct,
        best_distance_km: bestDistanceKm,
        best_accuracy_pct: bestAccuracyPct,
      },
    }).then((result) => {
      if (result?.earthGuessAverages) {
        setEarthGuessAverages(result.earthGuessAverages);
      }
      if (result?.pointsAwarded) {
        setPointsAwarded(result.pointsAwarded);
        setFirstGameBonus(Boolean(result.firstGameBonus));
        setSeasonPoints((prev) => (prev ?? 0) + result.pointsAwarded);
        setLifetimePoints((prev) => (prev ?? 0) + result.pointsAwarded);
      }
    });

    roundTrackingIdRef.current = null;
  }, [setLifetimePoints, setSeasonPoints, stopActiveTimer]);

  const resetForFreshRound = useCallback((mode) => {
    setGuesses([]);
    setSelectedGuess(null);
    setAnswer(null);
    setError('');
    setIsSubmittingGuess(false);
    setPointsAwarded(0);
    setFirstGameBonus(false);
    setEarthGuessAverages(null);
    setElapsedMs(0);
    setEarthRiddle('');
    setEarthRiddleError('');
    setIsLoadingEarthRiddle(false);
    setIsDemoRound(false);
    setDemoAnswer(null);
    setRound(createInitialRound(mode));
  }, []);

  const hydrateRound = useCallback((data) => {
    setRound({
      roundId: data.roundId,
      mode: data.mode,
      status: data.status || 'active',
      maxAttempts: data.maxAttempts,
      attemptsUsed: data.attemptsUsed,
      revealStep: data.revealStep,
      zoomLevels: Array.isArray(data.zoomLevels) && data.zoomLevels.length > 0
        ? data.zoomLevels
        : getEarthZoomLevels(data.mode),
      imageDataUrl: data.imageDataUrl,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return undefined;

    (async () => {
      try {
        const resumed = await resumeEarthRound();
        if (cancelled) return;
        if (!resumed?.roundId || !resumed?.imageDataUrl) return;

        setSelectedMode(resumed.mode || EARTH_MODE_IMPOSSIBLE_ISH);
        hydrateRound(resumed);
        setIsDemoRound(false);
        setDemoAnswer(null);
        setGuesses(
          Array.isArray(resumed.guesses)
            ? resumed.guesses.map((g) => ({
                attemptNumber: g.attemptNumber,
                distanceKm: g.distanceKm,
                accuracyPct: g.accuracyPct,
                isCorrect: g.isCorrect,
                guessLat: g.guessLat,
                guessLng: g.guessLng,
              }))
            : [],
        );
        setPhase('playing');
        beginLeaderboardTracking(resumed.mode || EARTH_MODE_IMPOSSIBLE_ISH);
      } catch (err) {
        const isNoRound = err?.code === 'NO_ACTIVE_ROUND' || err?.status === 404;
        if (!isNoRound && !cancelled) {
          setError(err?.message || 'Failed to resume Earth round.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [beginLeaderboardTracking, hydrateRound, user?.id]);

  useEffect(() => {
    return () => {
      stopActiveTimer();
      roundTrackingIdRef.current = null;
    };
  }, [stopActiveTimer]);

  const startGameForMode = useCallback(async (modeToStartRaw) => {
    const modeToStart = typeof modeToStartRaw === 'string' ? modeToStartRaw : selectedMode;

    if (!user) {
      roundTrackingIdRef.current = null;
      stopActiveTimer();
      const demoRound = pickRandomEarthDemoRound(lastDemoRoundIdRef.current);
      lastDemoRoundIdRef.current = demoRound.id;
      resetForFreshRound(EARTH_MODE_IMPOSSIBLE);
      setSelectedMode(EARTH_MODE_IMPOSSIBLE);
      setIsDemoRound(true);
      setDemoAnswer(demoRound.answer);
      setRound({
        roundId: `earth-demo:${demoRound.id}`,
        mode: EARTH_MODE_IMPOSSIBLE,
        status: 'active',
        maxAttempts: 1,
        attemptsUsed: 0,
        revealStep: 0,
        zoomLevels: getEarthZoomLevels(EARTH_MODE_IMPOSSIBLE),
        imageDataUrl: demoRound.imageSrc,
      });
      setPhase('playing');
      return;
    }

    if (!canAfford) {
      setError('Not enough coins.');
      return;
    }

    resetForFreshRound(modeToStart);
    setSelectedMode(modeToStart);
    setPhase('loading');

    try {
      const data = await startEarthRound({ mode: modeToStart });
      if (!data.roundId || !data.imageDataUrl) {
        throw new Error('Invalid Earth round response.');
      }

      hydrateRound(data);
      setIsDemoRound(false);
      setDemoAnswer(null);
      setSelectedMode(data.mode || modeToStart);
      setPhase('playing');
      await refreshCoins?.();
      beginLeaderboardTracking(data.mode || modeToStart);
    } catch (err) {
      setPhase('idle');
      setError(err?.message || 'Failed to start Earth round.');
      await refreshCoins?.();
    }
  }, [
    beginLeaderboardTracking,
    canAfford,
    hydrateRound,
    refreshCoins,
    resetForFreshRound,
    selectedMode,
    stopActiveTimer,
    user,
  ]);

  const handleStartGame = useCallback(async () => {
    await startGameForMode(selectedMode);
  }, [selectedMode, startGameForMode]);

  const handleSubmitGuess = useCallback(async () => {
    if (phase !== 'playing' || !round.roundId || !selectedGuess || isSubmittingGuess) {
      return;
    }

    setError('');
    setIsSubmittingGuess(true);

    try {
      if (isDemoRound) {
        if (!demoAnswer) {
          throw new Error('Demo answer is unavailable.');
        }

        const rawDistanceKm = haversineDistanceKm(
          selectedGuess.lat,
          selectedGuess.lng,
          demoAnswer.lat,
          demoAnswer.lng,
        );
        if (!Number.isFinite(rawDistanceKm)) {
          throw new Error('Failed to calculate demo distance.');
        }

        const distanceKm = Number(rawDistanceKm.toFixed(1));
        const accuracyPct = distanceKmToAccuracyPct(distanceKm);
        const isCorrect = isEarthGuessCorrect(distanceKm);

        const nextGuess = {
          attemptNumber: 1,
          distanceKm,
          accuracyPct,
          isCorrect,
          guessLat: selectedGuess.lat,
          guessLng: selectedGuess.lng,
        };

        setGuesses([nextGuess]);
        setSelectedGuess(null);
        setRound((prev) => ({
          ...prev,
          status: isCorrect ? 'solved' : 'failed',
          attemptsUsed: 1,
          maxAttempts: 1,
          revealStep: 1,
          zoomLevels: getEarthZoomLevels(EARTH_MODE_IMPOSSIBLE),
        }));
        setAnswer(demoAnswer);
        setPhase('finished');
        return;
      }

      const result = await submitEarthGuess({
        roundId: round.roundId,
        guessLat: selectedGuess.lat,
        guessLng: selectedGuess.lng,
      });

      const nextGuess = {
        attemptNumber: result.attemptNumber,
        distanceKm: result.distanceKm,
        accuracyPct: result.accuracyPct,
        isCorrect: result.isCorrect,
        guessLat: selectedGuess.lat,
        guessLng: selectedGuess.lng,
      };

      const allGuesses = [...guesses, nextGuess];
      const allDistances = allGuesses
        .map((g) => Number(g.distanceKm))
        .filter(Number.isFinite);
      const allAccuracies = allGuesses
        .map((g) => Number(g.accuracyPct))
        .filter(Number.isFinite);

      const bestDistanceKm = allDistances.length > 0 ? Math.min(...allDistances) : null;
      const bestAccuracyPct = allAccuracies.length > 0 ? Math.max(...allAccuracies) : null;

      setGuesses(allGuesses);
      setSelectedGuess(null);
      setRound((prev) => ({
        ...prev,
        status: result.status || prev.status,
        attemptsUsed: result.attemptsUsed,
        maxAttempts: result.maxAttempts,
        revealStep: result.revealStep,
        zoomLevels: Array.isArray(result.zoomLevels) && result.zoomLevels.length > 0
          ? result.zoomLevels
          : prev.zoomLevels,
      }));

      if (result.answer) {
        setAnswer(result.answer);
      }

      if (result.roundFinished) {
        setPhase('finished');
        finalizeLeaderboardTracking({
          won: Boolean(result.isCorrect),
          guessesUsed: result.attemptsUsed,
          finalDistanceKm: result.distanceKm,
          finalAccuracyPct: result.accuracyPct,
          bestDistanceKm,
          bestAccuracyPct,
          mode: round.mode,
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to submit guess.');
    } finally {
      setIsSubmittingGuess(false);
    }
  }, [
    demoAnswer,
    finalizeLeaderboardTracking,
    guesses,
    isDemoRound,
    isSubmittingGuess,
    phase,
    round.mode,
    round.roundId,
    selectedGuess,
  ]);

  const handleReveal = useCallback(async () => {
    if (phase !== 'playing' || !round.roundId) return;

    setError('');
    try {
      if (isDemoRound) {
        if (!demoAnswer) {
          throw new Error('Demo answer is unavailable.');
        }
        setAnswer(demoAnswer);
        setRound((prev) => ({
          ...prev,
          status: 'revealed',
          revealStep: 1,
        }));
        setPhase('finished');
        return;
      }

      const reveal = await revealEarthRound({ roundId: round.roundId });
      setAnswer(reveal.answer || null);
      setRound((prev) => ({
        ...prev,
        status: reveal.status || 'revealed',
      }));
      setPhase('finished');

      const finalDistance = Number.isFinite(Number(latestGuess?.distanceKm))
        ? Number(latestGuess.distanceKm)
        : null;
      const finalAccuracy = Number.isFinite(Number(latestGuess?.accuracyPct))
        ? Number(latestGuess.accuracyPct)
        : finalDistance !== null
          ? distanceKmToAccuracyPct(finalDistance)
          : 0;
      const distances = guesses
        .map((g) => Number(g.distanceKm))
        .filter(Number.isFinite);
      const accuracies = guesses
        .map((g) => Number(g.accuracyPct))
        .filter(Number.isFinite);

      finalizeLeaderboardTracking({
        won: false,
        guessesUsed: guesses.length,
        finalDistanceKm: finalDistance,
        finalAccuracyPct: finalAccuracy,
        bestDistanceKm: distances.length > 0 ? Math.min(...distances) : null,
        bestAccuracyPct: accuracies.length > 0 ? Math.max(...accuracies) : null,
        mode: round.mode,
      });
    } catch (err) {
      setError(err?.message || 'Failed to reveal answer.');
    }
  }, [
    demoAnswer,
    finalizeLeaderboardTracking,
    guesses,
    isDemoRound,
    latestGuess,
    phase,
    round.mode,
    round.roundId,
  ]);

  const handleUseRiddleMachine = useCallback(async () => {
    if (phase !== 'playing' || !round.roundId || isLoadingEarthRiddle) return;
    if (isDemoRound) {
      setEarthRiddleError('Riddle Machine is disabled in demo. Sign up for full mode.');
      return;
    }
    if (!canAffordRiddleMachine) {
      setEarthRiddleError(
        `Not enough coins for Riddle Machine (${EARTH_RIDDLE_MACHINE_COST} coins).`,
      );
      return;
    }

    setEarthRiddleError('');
    setIsLoadingEarthRiddle(true);

    try {
      const result = await requestEarthRiddle({ roundId: round.roundId });
      if (!result?.riddle) {
        setEarthRiddleError('No riddle was returned. Try again.');
        return;
      }

      setEarthRiddle(result.riddle);
      await refreshCoins?.();
    } catch (err) {
      if (err?.code === 'NOT_ENOUGH_COINS') {
        setEarthRiddleError(
          `Not enough coins for Riddle Machine (${EARTH_RIDDLE_MACHINE_COST} coins).`,
        );
      } else if (err?.code === 'UNAUTHORIZED') {
        setEarthRiddleError('Please sign in again to use the Riddle Machine.');
      } else {
        setEarthRiddleError(err?.message || 'Failed to generate a country riddle.');
      }
      await refreshCoins?.();
    } finally {
      setIsLoadingEarthRiddle(false);
    }
  }, [
    canAffordRiddleMachine,
    isDemoRound,
    isLoadingEarthRiddle,
    phase,
    refreshCoins,
    round.roundId,
  ]);

  const handlePlayAgain = useCallback(() => {
    if (phase !== 'finished') return;
    if (isDemoRound && !user) {
      startGameForMode(EARTH_MODE_IMPOSSIBLE);
      return;
    }
    startGameForMode(round.mode || selectedMode);
  }, [isDemoRound, phase, round.mode, selectedMode, startGameForMode, user]);

  const handlePlayImpossibleIshInstead = useCallback(() => {
    if (phase !== 'finished') return;
    startGameForMode(EARTH_MODE_IMPOSSIBLE_ISH);
  }, [phase, startGameForMode]);

  return (
    <div className="earth-game">
      <div className="earth-top-bar">
        <div className="earth-cost-pill">
          {!user || isDemoRound ? 'Demo mode • Free' : `${ZOOMED_IN_EARTH_COST} coins / game`}
        </div>
        <div className="earth-timer-pill" title="Active play time">
          Time: {formatDuration(elapsedMs)}
        </div>
      </div>

      {error && <div className="earth-error">{error}</div>}

      {phase === 'idle' && (
        <div className="earth-intro-card">
          <h2>Zoomed In Earth</h2>
          <p>
            Guess the real-world location from a zoomed-in Street View photo. Drop a
            map pin, submit your guess, and compare your speed + accuracy.
          </p>
          {!user && (
            <p className="earth-demo-note">{EARTH_DEMO_NOTE}</p>
          )}

          {user ? (
            <EarthModeSelector
              mode={selectedMode}
              onChange={setSelectedMode}
              disabled={phase === 'loading'}
            />
          ) : (
            <div className="earth-demo-lock-pill">
              Demo is locked to Impossible mode (1 guess).
            </div>
          )}

          <button
            type="button"
            className="earth-primary-btn"
            onClick={handleStartGame}
            disabled={Boolean(user) && !canAfford}
          >
            {!user
              ? GUEST_DEMO_BUTTON_LABEL
              : !canAfford
                ? 'Not Enough Coins'
                : `Start Game - ${selectedModeLabel} - ${ZOOMED_IN_EARTH_COST} coins`}
          </button>
        </div>
      )}

      {phase === 'loading' && (
        <div className="earth-loading">
          <div className="earth-spinner" />
          <p>Finding a Street View location...</p>
        </div>
      )}

      {(phase === 'playing' || phase === 'finished') && (
        <>
          {isDemoRound && (
            <div className="earth-demo-banner">{EARTH_DEMO_NOTE}</div>
          )}
          <div className="earth-main-layout">
            <div className="earth-image-panel">
              <div className="earth-image-frame">
                <img
                  src={round.imageDataUrl}
                  alt="Zoomed In Earth round"
                  className="earth-image"
                  style={{
                    width: `${displayZoom}%`,
                    height: `${displayZoom}%`,
                  }}
                />
              </div>
              <div className="earth-image-meta">
                <span>Reveal step: {round.revealStep + 1}</span>
                <span>
                  Attempts: {round.attemptsUsed}/{round.maxAttempts}
                </span>
              </div>
            </div>

            <div className="earth-map-panel">
              <EarthGuessMap
                guesses={guesses}
                selectedGuess={selectedGuess}
                answer={phase === 'finished' ? answer : null}
                onSelectGuess={setSelectedGuess}
                disabled={phase !== 'playing' || isSubmittingGuess}
              />

              {phase === 'playing' && (
                <div className="earth-controls-row">
                  <button
                    type="button"
                    className="earth-primary-btn"
                    onClick={handleSubmitGuess}
                    disabled={!selectedGuess || isSubmittingGuess}
                  >
                    {isSubmittingGuess ? 'Submitting...' : 'Submit Guess'}
                  </button>
                  <button
                    type="button"
                    className="earth-secondary-btn"
                    onClick={handleReveal}
                  >
                    Give Up
                  </button>
                  <button
                    type="button"
                    className="earth-secondary-btn"
                    onClick={handleUseRiddleMachine}
                    disabled={
                      isSubmittingGuess ||
                      isLoadingEarthRiddle ||
                      !canAffordRiddleMachine ||
                      isDemoRound
                    }
                  >
                    {isDemoRound
                      ? 'Riddle Machine (Real mode only)'
                      : isLoadingEarthRiddle
                        ? 'Generating Country Riddle...'
                        : `Riddle Machine (${EARTH_RIDDLE_MACHINE_COST} coins)`}
                  </button>
                </div>
              )}

              {(earthRiddle || earthRiddleError || isLoadingEarthRiddle) && (
                <div className="earth-riddle-panel">
                  <h4>Riddle Machine</h4>
                  {isLoadingEarthRiddle && (
                    <p className="earth-riddle-loading">Crafting a country riddle...</p>
                  )}
                  {earthRiddleError && (
                    <p className="earth-riddle-error">{earthRiddleError}</p>
                  )}
                  {earthRiddle && (
                    <p className="earth-riddle-text">{earthRiddle}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {(phase === 'playing' || phase === 'finished') && (
        <EarthResultsPanel
          guesses={guesses}
          maxAttempts={round.maxAttempts}
          status={phase === 'playing' ? 'active' : round.status}
        />
      )}

      {phase === 'finished' && (
        <div className="earth-finished-card">
          <h3>
            {latestGuess?.isCorrect ? 'Great guess!' : 'Round complete'}
          </h3>
          {answer && (
            <p>
              Answer: {answer.lat.toFixed(4)}, {answer.lng.toFixed(4)}
              {answer.country ? ` (${answer.country})` : ''}
            </p>
          )}
          {!isDemoRound && (
            <>
              <RewardSummary
                pointsAwarded={pointsAwarded}
                firstGameBonus={firstGameBonus}
              />
              <EarthAverageGuessSummary
                mode={round.mode}
                averages={earthGuessAverages}
              />
            </>
          )}
          <div className="earth-finished-actions">
            <button
              type="button"
              className="earth-primary-btn"
              onClick={handlePlayAgain}
            >
              {isDemoRound ? 'Play Next Demo' : 'Play Again'}
            </button>
            {!isDemoRound && round.mode !== EARTH_MODE_IMPOSSIBLE_ISH && (
              <button
                type="button"
                className="earth-secondary-btn"
                onClick={handlePlayImpossibleIshInstead}
              >
                Play Impossible-ish Instead
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoomedInEarthGame;
